if (!customElements.get('quick-order-list-collection')) {
  customElements.define(
    'quick-order-list-collection',
    class QuickOrderListCollection extends HTMLElement {
      constructor() {
        super();
        this.cart = { items: [], total_price: 0, item_count: 0 };
        this.variantPrices = new Map();
        this.initializeVariantPrices();
        this.initEventListeners();
      }

      initializeVariantPrices() {
        this.querySelectorAll('.quick-order-list__row').forEach(row => {
          const variantId = row.dataset.variantId;
          const priceElement = row.querySelector('.quick-order-list__price .price');
          
          if (priceElement) {
            const priceText = priceElement.textContent.trim();
            const priceInCents = this.parsePriceToCents(priceText);
            this.variantPrices.set(variantId, priceInCents);
          }
        });
      }

      parsePriceToCents(priceString) {
        let cleaned = priceString.replace(/[^0-9.,]/g, '');
        
        if (cleaned.includes(',') && cleaned.includes('.')) {
          cleaned = cleaned.replace(/,/g, '');
        } else if (cleaned.includes(',')) {
          const parts = cleaned.split(',');
          if (parts[1] && parts[1].length === 2) {
            cleaned = cleaned.replace(',', '.');
          } else {
            cleaned = cleaned.replace(/,/g, '');
          }
        }
        
        const price = parseFloat(cleaned);
        
        return Math.round(price * 100);
      }

      initEventListeners() {
        this.querySelectorAll('.quantity__input').forEach(input => {
          input.addEventListener('change', this.handleQuantityChange.bind(this));
          input.addEventListener('input', this.handleQuantityInput.bind(this));
        });

        this.querySelectorAll('.quantity__button').forEach(button => {
          button.addEventListener('click', this.handleQuantityButton.bind(this));
        });

        this.querySelectorAll('.quick-order-list__remove-button').forEach(button => {
          button.addEventListener('click', this.handleRemoveItem.bind(this));
        });

        const removeAllBtn = this.querySelector('[data-remove-all]');
        if (removeAllBtn) {
          removeAllBtn.addEventListener('click', this.handleRemoveAll.bind(this));
        }

        const addToCartBtn = this.querySelector('[data-add-to-cart]');
        if (addToCartBtn) {
          addToCartBtn.addEventListener('click', this.handleAddToCart.bind(this));
        }
      }

      handleQuantityButton(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const quantityInput = button.closest('quantity-input');
        const input = quantityInput.querySelector('.quantity__input');
        const currentValue = parseInt(input.value) || 0;
        const isPlus = button.name === 'plus';
        
        const newValue = isPlus ? currentValue + 0 : Math.max(0, currentValue - 0);
        input.value = newValue;
        
        const changeEvent = new Event('change', { bubbles: true });
        input.dispatchEvent(changeEvent);
      }

      handleQuantityInput(event) {
        const input = event.target;
        let value = parseInt(input.value) || 0;
        
        if (value < 0) {
          value = 0;
          input.value = value;
        }
      }

      handleQuantityChange(event) {
        const input = event.target;
        const variantId = input.dataset.variantId;
        const quantity = parseInt(input.value) || 0;
        
        this.updateVariantTotal(variantId, quantity);
        this.updateCartTotals();
      }

      updateVariantTotal(variantId, quantity) {
        const totalElement = this.querySelector(`[data-variant-total="${variantId}"]`);
        if (!totalElement) return;

        const priceInCents = this.variantPrices.get(variantId) || 0;
        const totalInCents = priceInCents * quantity;
        
        totalElement.textContent = this.formatMoney(totalInCents);
      }

      updateCartTotals() {
        let totalItems = 0;
        let totalPriceInCents = 0;

        this.querySelectorAll('.quantity__input').forEach(input => {
          const quantity = parseInt(input.value) || 0;
          const variantId = input.dataset.variantId;
          const priceInCents = this.variantPrices.get(variantId) || 0;
          
          totalItems += quantity;
          totalPriceInCents += priceInCents * quantity;
        });

        const totalItemsElement = this.querySelector('[data-total-items]');
        const totalPriceElement = this.querySelector('[data-total-price]');

        if (totalItemsElement) {
          totalItemsElement.textContent = totalItems;
        }

        if (totalPriceElement) {
          totalPriceElement.textContent = this.formatMoney(totalPriceInCents);
        }
      }

      formatMoney(cents) {
        const dollars = cents / 100;
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(dollars);
      }

      updateMessage(type, itemCount = 0) {
        const messageWrapper = this.querySelector('.quick-order-list__message');
        const messageText = this.querySelector('.quick-order-list__message-text');
        const messageIcon = this.querySelector('.quick-order-list__message-icon');

        if (!messageText || !messageWrapper) return;
        messageWrapper.classList.remove('success', 'error', 'active');
        messageIcon?.classList.add('hidden');

        let message = '';

        switch (type) {
          case 'added':
            message = itemCount > 1
              ? `${itemCount} items added to cart`
              : 'Item added to cart';

            messageWrapper.classList.add('success', 'active');
            messageIcon?.classList.remove('hidden');
            break;

          case 'updated':
            message = 'Cart updated';
            messageWrapper.classList.add('active');
            break;

          case 'removed':
            message = 'Item removed';
            messageWrapper.classList.add('active');
            break;

          case 'cleared':
            message = 'All items removed';
            messageWrapper.classList.add('active');
            break;

          case 'error':
            message = itemCount || 'An error occurred';
            messageWrapper.classList.add('error', 'active');
            break;
        }

        messageText.textContent = message;

        setTimeout(() => {
          messageWrapper.classList.remove('success', 'error', 'active');
          messageIcon?.classList.add('hidden');
        }, 1800);
      }

      handleRemoveItem(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;
        
        const input = this.querySelector(`.quantity__input[data-variant-id="${variantId}"]`);
        if (input) {
          input.value = 0;
          const changeEvent = new Event('change', { bubbles: true });
          input.dispatchEvent(changeEvent);
          this.updateMessage('removed');
        }
      }

      handleRemoveAll(event) {
        event.preventDefault();
        
        const hasItems = Array.from(this.querySelectorAll('.quantity__input'))
          .some(input => parseInt(input.value) > 0);
        
        if (!hasItems) {
          this.updateMessage('error', 'No items to remove');
          return;
        }

    
          this.querySelectorAll('.quantity__input').forEach(input => {
            input.value = 0;
          });
          
          this.updateCartTotals();
          this.updateMessage('cleared');
          
          // Update all variant totals
          this.querySelectorAll('[data-variant-total]').forEach(totalElement => {
            totalElement.textContent = this.formatMoney(0);
          });
      
      }

      async handleAddToCart(event) {
        event.preventDefault();
        
        const items = [];
        
        this.querySelectorAll('.quantity__input').forEach(input => {
          const quantity = parseInt(input.value) || 0;
          if (quantity > 0) {
            items.push({
              id: parseInt(input.dataset.variantId),
              quantity: quantity
            });
          }
        });

        if (items.length === 0) {
          this.updateMessage('error', 'Please add at least one item');
          return;
        }

        try {
          this.toggleLoading(true);
          
          const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ items })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || 'Failed to add items to cart');
          }

          const data = await response.json();
          console.log('Success:', data);
          
          const pageResponse = await fetch('/');
          const html = await pageResponse.text();
          
          let parser = new DOMParser();
          let doc = parser.parseFromString(html, 'text/html');

          const cartDrawer = document.querySelector('cart-drawer');
          const cartCount = document.querySelector('#cart-icon-bubble');
          
          if (cartDrawer && doc.querySelector('cart-drawer')) {
            cartDrawer.innerHTML = doc.querySelector('cart-drawer').innerHTML;
            cartDrawer.classList.remove('is-empty');
            cartDrawer.classList.add('active');
          }
          
          if (cartCount && doc.querySelector('#cart-icon-bubble')) {
            cartCount.innerHTML = doc.querySelector('#cart-icon-bubble').innerHTML;
          }
          
          document.body.classList.add('overflow-hidden');
          
          this.updateMessage('added', items.length);
          
          this.querySelectorAll('.quantity__input').forEach(input => {
            input.value = 0;
          });
          this.updateCartTotals();
          
          this.querySelectorAll('[data-variant-total]').forEach(totalElement => {
            totalElement.textContent = this.formatMoney(0);
          });
          
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
          
        } catch (error) {
          console.error('Add to cart error:', error);
          this.updateMessage('error', error.message || 'Failed to add items to cart');
          alert("Error adding to cart. Please try again.");
        } finally {
          this.toggleLoading(false);
        }
      }



      showError(message) {
        const errorElement = this.querySelector('.quick-order-list-error');
        if (!errorElement) return;

        const template = document.getElementById('QuickOrderListErrorTemplate');
        if (!template) return;

        const errorContent = template.content.cloneNode(true);
        errorContent.querySelector('.quick-order-list-error-message').textContent = message;
        
        errorElement.innerHTML = '';
        errorElement.appendChild(errorContent);

        setTimeout(() => {
          errorElement.innerHTML = '';
        }, 5000);
      }

      toggleLoading(loading) {
        const addToCartBtn = this.querySelector('[data-add-to-cart]');
        if (addToCartBtn) {
          addToCartBtn.disabled = loading;
          addToCartBtn.textContent = loading ? 'Adding...' : (addToCartBtn.dataset.originalText || 'Add to Cart');
          if (!loading && !addToCartBtn.dataset.originalText) {
            addToCartBtn.dataset.originalText = addToCartBtn.textContent;
          }
        }

        const removeAllBtn = this.querySelector('[data-remove-all]');
        if (removeAllBtn) {
          removeAllBtn.disabled = loading;
        }
      }
    }
  );

  const buttons = document.querySelectorAll(".bulk-collection-btn");
  const rows = document.querySelectorAll(".quick-order-list__row");

  function filterCollection(handle) {
    rows.forEach(row => {
      row.style.display = row.dataset.collection === handle ? "" : "none";
    });
  }

  const firstBtn = document.querySelector(".bulk-collection-btn.is-active");
  if (firstBtn) filterCollection(firstBtn.dataset.collectionHandle);

  buttons.forEach(btn => {
    btn.addEventListener("click", () => {
      buttons.forEach(b => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      filterCollection(btn.dataset.collectionHandle);
      AOS.init();
    });
  });

}